/*
  TWRD recommendations rail (PDP module B).

  Lazy: the HTML ships a height spacer and a visually hidden list of product
  links, not the cards. When the section is within 600px of the viewport we
  ask the Shopify Product Recommendations API for the same section, rendered
  server side, so the cards keep the theme's own product-card markup (wishlist
  heart, hover, quick shop). If the API returns nothing, the section itself
  answers with the same-family collection as full cards.
*/
(function () {
  if (window.__twrdRecsInit) return;
  window.__twrdRecsInit = true;

  function els(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function TwrdRecs(root) {
    this.root = root;
    this.track = root.querySelector('[data-twrd-recs-track]');
    this.thumb = root.querySelector('[data-twrd-recs-thumb]');
    this.buttons = els(root, '[data-twrd-recs-dir]');
    if (!this.track) return;

    this.onScroll = this.paint.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    var self = this;
    this.buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.page(parseInt(btn.getAttribute('data-twrd-recs-dir'), 10) || 1);
      });
    });

    this.watch();
    this.bindTabs();
    this.paint();
  }

  TwrdRecs.prototype.step = function () {
    var first = this.track.firstElementChild;
    if (!first) return 240;
    var styles = window.getComputedStyle(this.track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  TwrdRecs.prototype.page = function (dir) {
    var perPage = Math.max(1, Math.floor(this.track.clientWidth / this.step()));
    this.track.scrollBy({ left: dir * this.step() * perPage, behavior: 'smooth' });
  };

  TwrdRecs.prototype.paint = function () {
    /* Pending: the cards are not loaded yet. Keep the scroll bar and arrows in
       their loaded layout (arrows disabled) so the section height does not
       change when the cards arrive. */
    if (this.root.hasAttribute('data-twrd-recs-pending')) {
      this.root.removeAttribute('data-no-overflow');
      this.buttons.forEach(function (btn) { btn.setAttribute('disabled', ''); });
      return;
    }
    var max = this.track.scrollWidth - this.track.clientWidth;
    var overflowing = max > 2;
    if (overflowing) this.root.removeAttribute('data-no-overflow');
    else this.root.setAttribute('data-no-overflow', '');

    if (this.thumb) {
      var ratio = overflowing ? this.track.clientWidth / this.track.scrollWidth : 1;
      var progress = overflowing ? this.track.scrollLeft / max : 0;
      var travel = (1 - ratio) * this.track.clientWidth;
      this.thumb.style.width = Math.max(ratio * 100, 8) + '%';
      this.thumb.style.transform = 'translateX(' + progress * travel + 'px)';
    }

    var left = this.track.scrollLeft;
    this.buttons.forEach(function (btn) {
      var dir = parseInt(btn.getAttribute('data-twrd-recs-dir'), 10) || 1;
      var done = dir < 0 ? left <= 2 : left >= max - 2;
      if (!overflowing || done) btn.setAttribute('disabled', '');
      else btn.removeAttribute('disabled');
    });
  };

  /* Swap freshly rendered cards in. Returns true when the rail has cards. */
  TwrdRecs.prototype.swap = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-twrd-recs-track]');
    if (!fresh || !fresh.children.length) return false;
    this.track.innerHTML = fresh.innerHTML;
    this.takeTabs(doc);
    this.personalise();
    this.root.removeAttribute('data-twrd-recs-fallback');
    this.root.removeAttribute('data-twrd-recs-pending');
    var seo = this.root.querySelector('[data-twrd-recs-seo]');
    if (seo && seo.parentNode) seo.parentNode.removeChild(seo);
    this.paint();
    /* Wishlist hearts on freshly injected cards paint on this event. */
    try {
      document.dispatchEvent(new CustomEvent('twrd:wishlist:change', {
        detail: { count: (window.TwrdWishlist ? window.TwrdWishlist.count() : 0) }
      }));
    } catch (_) {}
    return true;
  };

  /* TWRD 2026-10-22: tabs. The fetched render carries the tab buttons and one
     <template> of cards per extra tab. A tab swaps the rail in place. */
  TwrdRecs.prototype.takeTabs = function (doc) {
    var tabs = this.root.querySelector('[data-twrd-recs-tabs]');
    var freshTabs = doc.querySelector('[data-twrd-recs-tabs]');
    var srcs = this.root.querySelector('[data-twrd-recs-srcs]');
    var freshSrcs = doc.querySelector('[data-twrd-recs-srcs]');
    if (!tabs || !freshTabs) return;
    tabs.innerHTML = freshTabs.innerHTML;
    if (freshSrcs) {
      if (!srcs) {
        srcs = document.createElement('div');
        srcs.hidden = true;
        srcs.setAttribute('data-twrd-recs-srcs', '');
        this.track.parentNode.insertBefore(srcs, this.track.nextSibling);
      }
      srcs.innerHTML = freshSrcs.innerHTML;
    }
    this.bindTabs();
  };

  TwrdRecs.prototype.bindTabs = function () {
    var self = this;
    var tabs = this.root.querySelector('[data-twrd-recs-tabs]');
    if (!tabs || tabs.__twrdBound) return;
    var list = els(tabs, '[role="tab"]');
    if (list.length < 2) { tabs.hidden = true; return; }
    tabs.__twrdBound = true;
    tabs.hidden = false;
    this.tabHtml = { rec: this.track.innerHTML };
    var active = list[0];
    this.track.setAttribute('aria-labelledby', active.id);

    function select(tab, focus) {
      if (tab === active) return;
      var key = tab.getAttribute('data-twrd-recs-tab');
      var html = self.tabHtml[key];
      if (html == null) {
        var tpl = self.root.querySelector('[data-twrd-recs-srcs] template[data-twrd-recs-src="' + key + '"]');
        html = tpl ? tpl.innerHTML : '';
        self.tabHtml[key] = html;
      }
      self.track.innerHTML = html;
      self.track.scrollLeft = 0;
      list.forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      self.track.setAttribute('aria-labelledby', tab.id);
      active = tab;
      if (focus) tab.focus();
      if (tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      self.paint();
      try {
        document.dispatchEvent(new CustomEvent('twrd:wishlist:change', {
          detail: { count: (window.TwrdWishlist ? window.TwrdWishlist.count() : 0) }
        }));
      } catch (_) {}
    }

    list.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight') n = list[(i + 1) % list.length];
        else if (e.key === 'ArrowLeft') n = list[(i - 1 + list.length) % list.length];
        else if (e.key === 'Home') n = list[0];
        else if (e.key === 'End') n = list[list.length - 1];
        if (n) { e.preventDefault(); select(n, true); }
      });
    });
  };

  /* TWRD 2026-10-22: personal tab 1. Built from this visitor's own history
     (localStorage twrd_recent, written by assets/twrd-recent.js), read only
     with preferences consent. Candidates come from the card pool section
     (sections/twrd-recs-pool.liquid) of the families the visitor viewed.
     Score: family view count, +3 for siblings and same-word pieces of recent
     views, times a seeded random factor (seed: day + handle + this page
     view, so the order holds within a view and shifts a little per visit).
     The server cards stay in place until the new set is ready. Without
     consent or history the server list is shuffled lightly. */
  function seeded(seedStr) {
    var h = 2166136261;
    for (var i = 0; i < seedStr.length; i++) { h ^= seedStr.charCodeAt(i); h = Math.imul(h, 16777619); }
    return function () {
      h += 0x6D2B79F5;
      var t = h;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function prefsAllowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    /* Region guard in snippets/twrd-consent.liquid: unknown region counts as no consent. */
    try { if (window.twrdConsent && typeof window.twrdConsent.held === 'function' && window.twrdConsent.held()) return false; } catch (_) { return false; }
    try { return !!(cp && typeof cp.preferencesProcessingAllowed === 'function' && cp.preferencesProcessingAllowed() === true); }
    catch (_) { return false; }
  }
  function history() {
    try {
      var d = JSON.parse(localStorage.getItem('twrd_recent') || '{}');
      return Array.isArray(d.p) ? d.p : [];
    } catch (_) { return []; }
  }
  var STOP = ' the a an of in is and for to my your be it on ';
  function mainWord(short) {
    var w = String(short || '').split(' ');
    for (var i = 0; i < w.length; i++) if (w[i] && STOP.indexOf(' ' + w[i] + ' ') < 0) return w[i];
    return '';
  }

  TwrdRecs.prototype.personalise = function () {
    if (this.personalised) return;
    this.personalised = true;
    var self = this;
    var handle = this.root.getAttribute('data-handle') || '';
    var pageCap = this.root.getAttribute('data-cap') === '1';
    var rand = seeded(new Date().toISOString().slice(0, 10) + '|' + handle + '|' + Math.floor((window.performance && performance.timeOrigin) || Date.now()));
    var hist = (prefsAllowed() ? history() : []).filter(function (x) { return x && x.h; });

    function finish(cellsHtml) {
      if (!cellsHtml) return;
      self.track.innerHTML = cellsHtml;
      if (self.tabHtml) self.tabHtml.rec = cellsHtml;
      self.track.scrollLeft = 0;
      self.paint();
      try {
        document.dispatchEvent(new CustomEvent('twrd:wishlist:change', {
          detail: { count: (window.TwrdWishlist ? window.TwrdWishlist.count() : 0) }
        }));
      } catch (_) {}
    }

    if (!hist.length) {
      /* Light shuffle of the server list: a few neighbour swaps. */
      var cells = els(this.track, '.twrd-recs__cell');
      if (cells.length < 3) return;
      for (var i = 0; i < cells.length - 1; i++) {
        if (rand() < 0.35) { var t = cells[i]; cells[i] = cells[i + 1]; cells[i + 1] = t; i++; }
      }
      finish(cells.map(function (c) { return c.outerHTML; }).join(''));
      return;
    }

    var now = Date.now();
    var famCount = {};
    var recent = {};
    var words = {};
    hist.forEach(function (x) {
      if (x.f) famCount[x.f] = (famCount[x.f] || 0) + 1;
      if (now - (x.t || 0) < 10 * 60 * 1000) recent[x.h] = true;
    });
    var fams = Object.keys(famCount).sort(function (a, b) { return famCount[b] - famCount[a]; }).slice(0, 3);
    var pageFam = this.root.getAttribute('data-family');
    if (pageFam && fams.indexOf(pageFam) < 0 && pageFam !== 'most-worn') fams.push(pageFam);
    var base = (this.root.getAttribute('data-pool-url') || '/collections/');

    Promise.all(fams.map(function (f) {
      return fetch(base + encodeURIComponent(f) + '?section_id=twrd-recs-pool', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
    })).then(function (pages) {
      var pool = {};
      pages.forEach(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        els(doc, '[data-pool-item]').forEach(function (n) {
          var h = n.getAttribute('data-handle');
          if (!pool[h]) pool[h] = n;
        });
      });
      /* Siblings and main words of the recently viewed products. */
      var sibs = {};
      hist.slice(0, 6).forEach(function (x) {
        var n = pool[x.h];
        if (!n) return;
        (n.getAttribute('data-sibs') || '').split(',').forEach(function (s) { if (s) sibs[s] = true; });
        var w = mainWord(n.getAttribute('data-short'));
        if (w) words[w] = true;
      });
      var scored = [];
      Object.keys(pool).forEach(function (h) {
        var n = pool[h];
        if (h === handle || recent[h]) return;
        if (n.getAttribute('data-up') === '1' && !(pageCap && n.getAttribute('data-iscap') === '1')) return;
        var score = famCount[n.getAttribute('data-family')] || 0.5;
        var short = ' ' + (n.getAttribute('data-short') || '') + ' ';
        if (sibs[h]) score += 3;
        Object.keys(words).forEach(function (w) { if (short.indexOf(' ' + w + ' ') >= 0) score += 3; });
        scored.push({ n: n, s: score * (0.6 + 0.8 * rand()) });
      });
      scored.sort(function (a, b) { return b.s - a.s; });
      var pick = scored.slice(0, 8);
      if (pick.length < 4) return; /* Too thin: keep the server list. */
      finish(pick.map(function (x) {
        var cell = x.n.querySelector('.twrd-recs__cell');
        if (cell) cell.setAttribute('data-twrd-personal', '');
        return cell ? cell.outerHTML : '';
      }).join(''));
    }).catch(function () { /* keep the server list */ });
  };

  /* Last resort: no cards could be fetched. Drop the spacer and show the
     plain link list so the section is never an empty gap. */
  TwrdRecs.prototype.giveUp = function () {
    this.root.removeAttribute('data-twrd-recs-pending');
    this.root.setAttribute('data-twrd-recs-failed', '');
    var ph = this.track.querySelector('.twrd-recs__cell--ph');
    if (ph && ph.parentNode) ph.parentNode.removeChild(ph);
    this.paint();
  };

  TwrdRecs.prototype.load = function () {
    if (this.started) return;
    this.started = true;
    var url = this.root.getAttribute('data-url');
    if (!url) { this.giveUp(); return; }
    var self = this;

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Recommendations request failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        if (!self.swap(html)) self.giveUp();
      })
      .catch(function (err) {
        /* No user facing error. The link list stays as the fallback. */
        if (window.console && console.warn) console.warn('[twrd-recs]', err);
        self.giveUp();
      });
  };

  /* The cards are fetched when the section is about to scroll into view.
     A section that already ships cards (theme editor, old markup) is left alone. */
  TwrdRecs.prototype.watch = function () {
    var self = this;
    if (!this.root.hasAttribute('data-twrd-recs-pending')) { this.load(); return; }
    if (!('IntersectionObserver' in window)) { this.load(); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          io.disconnect();
          self.load();
          return;
        }
      }
    }, { rootMargin: '600px 0px' });
    io.observe(this.root);
  };

  function boot(scope) {
    els(scope || document, '[data-twrd-recs]').forEach(function (root) {
      if (root.__twrdRecs) return;
      root.__twrdRecs = new TwrdRecs(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
